import { Formik } from 'formik';
import { type ComponentType, type Dispatch, type PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentStatus } from '../../../../../api/types';
import { useThemeComponents } from '../../../../../themes';
import { type DefaultLayoutProps } from '../../../../../themes/default/Standard/Layout';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import useAgreementsValidator from '../../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValidator';
import useAgreementsValues, {
    type AgreementValues,
} from '../../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';
import useGiftVoucherPaymentAgreements from '../AdyenPaymentPage/useGiftVoucherPaymentAgreements';
import { type GiftVoucherAction, type GiftVoucherState } from '../reducer';
import Inner from './Inner';
import useGiftVoucherPayGateDepositSubmission, { submitPayment } from './useGiftVoucherPayGateDepositSubmission';

export type GiftVoucherPayGatePaymentPageProps = {
    state: GiftVoucherState;
    dispatch: Dispatch<GiftVoucherAction>;
    CustomLayout: ComponentType<PropsWithChildren<DefaultLayoutProps>>;
    promoCodeModuleId?: string;
    applicationModuleId?: string;
};

const submitForm = ({ url, ...params }: { checksum: string; url: string; payRequestId: string }) => {
    const form = document.createElement('form');

    form.method = 'POST';
    form.action = url;

    const paramsMapping = {
        checksum: 'CHECKSUM',
        payRequestId: 'PAY_REQUEST_ID',
    };

    for (const key of Object.keys(params)) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = paramsMapping[key];
        hiddenField.value = params[key];

        form.appendChild(hiddenField);
    }

    document.body.appendChild(form);
    form.submit();
};

type PayGateFormValues = AgreementValues & { promoCodeId?: string };

const PayGatePaymentPage = ({ state, dispatch, CustomLayout, ...props }: GiftVoucherPayGatePaymentPageProps) => {
    const { token, giftVoucher } = state;
    const { t } = useTranslation(['paymentDetails']);
    const { notification } = useThemeComponents();
    const paymentAgreements = useGiftVoucherPaymentAgreements(state);
    const initialValues = useAgreementsValues(paymentAgreements);
    const validator = useAgreementsValidator(paymentAgreements);
    const validation = useValidator(validator);

    const submitPayGateDeposit = useGiftVoucherPayGateDepositSubmission();

    useEffect(() => {
        if ([PaymentStatus.Failed, PaymentStatus.Error].includes(giftVoucher.deposit.status)) {
            notification.error(t('paymentDetails:resultCode.error'));
        }
    }, [giftVoucher.deposit.status, notification, t]);

    const onSubmit = useHandleError(
        async (values: PayGateFormValues) => {
            notification.loading({
                content: t('paymentDetails:messages.paymentRedirecting'),
                duration: 0,
                key: 'primary',
            });
            const { promoCodeId, ...agreementValues } = values;

            if (giftVoucher.deposit.amount !== 0) {
                try {
                    // call payment submission api
                    const paymentResult = await submitPayment(agreementValues, token, promoCodeId);

                    if (paymentResult) {
                        // redirect to payment site
                        submitForm(paymentResult);

                        return;
                    }

                    // there's an error
                    notification.error(t('paymentDetails:resultCode.error'));

                    return;
                } catch {
                    notification.error(t('paymentDetails:resultCode.error'));

                    return;
                }
            }

            const result = await submitPayGateDeposit(token, agreementValues, false).finally(() => {
                notification.destroy('primary');
            });

            const { giftVoucher: latestGiftVoucher } = result;

            if (latestGiftVoucher.draftFlow.isDepositCompleted) {
                dispatch({
                    type: 'next',
                    token: result.token,
                    giftVoucher: latestGiftVoucher,
                });
            } else {
                // there's an error
                notification.warn(t('paymentDetails:resultCode.error'));
            }
        },
        [notification, t, giftVoucher.deposit.amount, submitPayGateDeposit, token, dispatch],
        {}
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={state.giftVoucher.deposit.amount > 0 ? validation : null}
        >
            <Inner CustomLayout={CustomLayout} dispatch={dispatch} state={state} {...props} />
        </Formik>
    );
};

export default PayGatePaymentPage;
